import React, { useEffect, useState, useCallback, useMemo } from 'react';
import css from './AlgoliaFilter.module.css';
import { IconCollection, Modal, RangeSlider } from '../../../../components';
import { FormattedMessage } from 'react-intl';

// Constants
const DEFAULT_PRICE_RANGE = [0, 10000];
const PRICE_STEP = 50;
const FILTER_SECTIONS = {
    category: 'Rental Categories',
    Condition_of_the_item: 'Condition of the Item',
    Trust_and_verification: 'Trust and Verification',
    access_to_rentals: 'Access to Rentals',
    cancelation_policy: 'Cancellation Policy',
    security_deposit: 'Security Deposit',
    reason_for_listing: 'Reason For Listing'
};

/**
 * Filter section component that renders a group of filter options
 */
const FilterSection = React.memo(({ title, items, selectedValue, onSelect }) => {
    if (!items || items.length === 0) return null;

    return (
        <div className={css.section}>
            <h3 className={css.sectionTitle}>{title}</h3>
            <div className={css.filterOptions}>
                {items.map(item => (
                    <button
                        key={item.value}
                        className={`${css.category} ${selectedValue === item.value ? css.categorySelected : ''}`}
                        onClick={() => onSelect(item.value)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
        </div>
    );
});

/**
 * Price range filter component
 */
const PriceRangeFilter = React.memo(({ availablePriceRange, priceRange, setPriceRange }) => {
    // Ensure price range values are properly ordered and within valid range
    const validPriceRange = [
        Math.max(availablePriceRange[0], Math.min(priceRange[0], availablePriceRange[1])),
        Math.max(availablePriceRange[0], Math.min(priceRange[1], availablePriceRange[1]))
    ];

    // Ensure min is not greater than max
    if (validPriceRange[0] > validPriceRange[1]) {
        validPriceRange[0] = validPriceRange[1];
    }

    return (
        <div className={css.section}>
            <h3 className={css.sectionTitle}>Price Range</h3>
            <div className={css.priceRange}>
                <RangeSlider
                    min={availablePriceRange[0]}
                    max={availablePriceRange[1]}
                    step={PRICE_STEP}
                    handles={validPriceRange}
                    onChange={setPriceRange}
                />
                <div className={css.priceLabels}>
                    <span>Minimum</span>
                    <span>Maximum</span>
                </div>
                <div className={css.priceLabels}>
                    <span className={css.inputBox}>${validPriceRange[0]}</span>
                    <span className={css.inputBox}>${validPriceRange[1]}</span>
                </div>
            </div>
        </div>
    )
});

/**
 * Main AlgoliaFilters component
 */
function AlgoliaFilters(props) {
    const { facets, manageDisableScrolling, setIsOpen, isOpen, handleShowRentals, selectedFilters, showResetButton, onResetFilters } = props;

    // State
    // The available price range is the price range from the facets
    const availablePriceRange = useMemo(() => {
        const priceFacet = facets?.['price.amount'];
        const min = (+priceFacet?.[0]?.value / 100) || 0;
        const max = (+priceFacet?.[priceFacet?.length - 1]?.value / 100) || 10000;
        return [min, max];
    }, [facets]);

    // The default price range is the price range from the selected filters
    const defaultPriceRange = useMemo(() => {
        if (selectedFilters?.['priceRange']) {
            const [min, max] = selectedFilters['priceRange'];
            // Ensure values are within available range and properly ordered
            return [
                Math.max(availablePriceRange[0], Math.min(min, availablePriceRange[1])),
                Math.max(availablePriceRange[0], Math.min(max, availablePriceRange[1]))
            ];
        }
        return availablePriceRange;
    }, [selectedFilters, availablePriceRange]);

    const [localFilters, setLocalFilters] = useState({});
    const [priceRange, setPriceRange] = useState(defaultPriceRange);

    // Initialize local filters when modal opens
    useEffect(() => {
        if (isOpen) {
            setLocalFilters(selectedFilters);
            setPriceRange(defaultPriceRange);
        }
    }, [isOpen, selectedFilters, defaultPriceRange]);

    // Handle filter selection
    const handleFilterSelect = useCallback((filterKey, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [filterKey]: value,
        }));
    }, []);

    // Handle clear all filters
    const handleClearAll = useCallback(() => {
        setLocalFilters({});
        setPriceRange(availablePriceRange);
    }, [availablePriceRange]);

    // Handle apply filters
    const handleApplyFilters = useCallback(() => {
        handleShowRentals({ ...localFilters, priceRange });
    }, [localFilters, priceRange, handleShowRentals]);

    // Handle modal close
    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    // Manage body scroll and z-index when modal is open
    useEffect(() => {
        const header = document.querySelector('header');
        const mobileBottomNavbar = document.getElementById('mobileBottomNavbar');

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            if (header) header.style.zIndex = '0';
            if (mobileBottomNavbar) mobileBottomNavbar.style.zIndex = '0';
        } else {
            document.body.style.overflow = '';
            if (header) header.style.zIndex = '';
            if (mobileBottomNavbar) mobileBottomNavbar.style.zIndex = '';
        }

        return () => {
            document.body.style.overflow = '';
            if (header) header.style.zIndex = '';
            if (mobileBottomNavbar) mobileBottomNavbar.style.zIndex = '';
        };
    }, [isOpen]);

    // Memoize filter sections to prevent unnecessary re-renders
    const filterSections = useMemo(() =>
        Object.entries(FILTER_SECTIONS).map(([key, title]) => (
            <FilterSection
                key={key}
                title={title}
                items={facets?.[key]}
                selectedValue={localFilters[key]}
                onSelect={(value) => handleFilterSelect(key, value)}
            />
        )),
        [facets, localFilters, handleFilterSelect]
    );

    const handlePriceRangeChange = useCallback((value) => {
        // Ensure values are within available range and properly ordered
        const validPriceRange = [
            Math.max(availablePriceRange[0], Math.min(value[0], availablePriceRange[1])),
            Math.max(availablePriceRange[0], Math.min(value[1], availablePriceRange[1]))
        ];

        // Ensure min is not greater than max
        if (validPriceRange[0] > validPriceRange[1]) {
            validPriceRange[0] = validPriceRange[1];
        }

        setPriceRange(validPriceRange);
    }, [availablePriceRange]);

    return (
        <>
            {showResetButton ? (
                <button className={css.resetFiltersButton} onClick={onResetFilters}>
                    <IconCollection name="reset_cross" /> Remove Filters
                </button>
            ) : (
                <button
                    className={css.moreFiltersLabel}
                    type="button"
                    onClick={() => setIsOpen(true)}
                >
                    <IconCollection name="filter_icon" />
                    <FormattedMessage id="AlgoliaFilters.filters" defaultMessage="Filters" />
                </button>
            )}

            <Modal
                className={css.moreFiltersModal}
                isOpen={isOpen}
                id="SearchPage"
                onManageDisableScrolling={manageDisableScrolling}
                onClose={handleClose}
            >
                <div className={css.filterModal}>
                    <div className={css.modalHeader}>
                        <h2 className={css.modalTitle}>
                            <FormattedMessage id="AlgoliaFilters.filters" defaultMessage="Filters" />
                        </h2>
                    </div>

                    <div className={css.modalBody}>
                        {filterSections}
                        <PriceRangeFilter availablePriceRange={availablePriceRange} priceRange={priceRange} setPriceRange={handlePriceRangeChange} />
                    </div>

                    <div className={css.footer}>
                        <button
                            type="button"
                            className={css.clearBtn}
                            onClick={handleClearAll}
                        >
                            <FormattedMessage id="AlgoliaFilters.clearAll" defaultMessage="Clear All" />
                        </button>
                        <button
                            type="button"
                            className={css.showBtn}
                            onClick={handleApplyFilters}
                        >
                            <FormattedMessage id="AlgoliaFilters.showRentals" defaultMessage="Show Rentals" />
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default React.memo(AlgoliaFilters);
